import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/store';

export interface BaseUnitTelemetryData {
  device_serial: string;
  firmware_version: string;
  timestamp: string;
  power_status: string;
  wifi_connected: boolean;
  wifi_signal_dbm: number;
  mqtt_connected: boolean;
  uptime_ms: number;
  uptime_hours: number;
  uptime_minutes: number;
  uptime_seconds: number;
  total_readings_received: number;
  readings_this_hour: number;
  num_sensors_configured: number;
  free_heap_bytes: number;
  heap_fragmentation: number;
  lora_processing_enabled: boolean;
  wifi_ssid: string;
  wifi_ip: string;
  wifi_mac: string;
  connection_type?: string;
  ethernet_connected?: boolean;
  ethernet_ip?: string;
  mqtt_broker: string;
  mqtt_port: number;
  mqtt_topic_base: string;
  battery_voltage?: number | null;
}

export interface UpdateTelemetryRequest {
  wifi_signal_dbm: number;
  readings_this_hour: number;
  free_heap_bytes: number;
}

export const baseUnitTelemetryApi = createApi({
  reducerPath: 'baseUnitTelemetryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://s3.ts1cloud.com/service3/telemetry/',
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const user = state.auth.user;
      
      if (user?.companyID && user?.companyName) {
        headers.set('x-company-id', user.companyID);
        headers.set('x-company-name', user.companyName);
      }
      
      return headers;
    },
  }),
  tagTypes: ['BaseUnitTelemetry'],
  endpoints: (builder) => ({
    getBaseUnitTelemetry: builder.query<BaseUnitTelemetryData, string>({
      query: (deviceSerial) => ({
        url: deviceSerial,
        method: 'GET',
      }),
      providesTags: (result, error, deviceSerial) => [
        { type: 'BaseUnitTelemetry', id: deviceSerial },
      ],
    }),
    updateBaseUnitTelemetry: builder.mutation<BaseUnitTelemetryData, { deviceSerial: string; data: UpdateTelemetryRequest }>({
      query: ({ deviceSerial, data }) => ({
        url: deviceSerial,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { deviceSerial }) => [
        { type: 'BaseUnitTelemetry', id: deviceSerial },
      ],
    }),
    // Latest base firmware published on firmware.ts1cloud.com (base/version)
    getBaseFirmwareLatest: builder.query<{ latestVersion: string }, string>({
      query: (deviceSerial) => ({
        url: `${deviceSerial}/firmware`,
        method: 'GET',
      }),
    }),
    // Sends the "updatebase" command; the base downloads, verifies and reboots itself
    updateBaseFirmware: builder.mutation<{ ok: boolean; topic: string }, string>({
      query: (deviceSerial) => ({
        url: `${deviceSerial}/update-firmware`,
        method: 'POST',
      }),
    }),
    // Super admin: which repeater build a conversion would install
    getRepeaterConversionInfo: builder.query<RepeaterConversionInfo, string>({
      query: (deviceSerial) => ({
        url: `${deviceSerial}/convert-to-repeater`,
        method: 'GET',
      }),
    }),
    // Super admin: OTA a base unit into a repeater (one-way; back to a base needs USB)
    convertBaseToRepeater: builder.mutation<
      { ok: boolean; topic: string; repeaterId: string; repeaterVersion: string; expiresAt: number },
      string
    >({
      query: (deviceSerial) => ({
        url: `${deviceSerial}/convert-to-repeater`,
        method: 'POST',
      }),
    }),
  }),
});

export interface RepeaterConversionInfo {
  repeaterVersion: string | null;
  repeaterId: string | null;
  conversionInProgress: { serial: string; expiresAt: number } | null;
}

export const {
  useGetBaseUnitTelemetryQuery,
  useUpdateBaseUnitTelemetryMutation,
  useGetBaseFirmwareLatestQuery,
  useUpdateBaseFirmwareMutation,
  useGetRepeaterConversionInfoQuery,
  useConvertBaseToRepeaterMutation,
} = baseUnitTelemetryApi;
